/**
 * The attorney coverage RECORD — the page that replaces the workbook.
 *
 * Until an operator can see and change this here, we have only COPIED Harrison's spreadsheet.
 * So this screen has to answer, without anyone opening a sheet:
 *   - who covers each state, including the states that are split in two
 *   - which rows we are unsure about, and WHY, in words someone can act on
 *   - which states have nobody at all
 *   - and, for a real member, who would actually get their question
 *
 * That last one matters most. A coverage list can look complete and still route nobody: Texas
 * is split North/South and the answer depends on the member's ZIP. The tester runs the REAL
 * server resolver — the same one routing will use — rather than re-deriving the rule here.
 */
import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import {
  listAttorneyDirectory,
  resolveAttorneyJurisdiction,
  upsertAttorneyDirectoryEntry,
  uncoveredJurisdictions,
  summariseByState,
  ALL_JURISDICTIONS,
  type AttorneyDirectoryRow,
  type ResolvedAttorney,
} from "../../services/attorneyDirectory.service";

interface Props {
  canWrite: boolean;
}

/** Excel treats a leading =, +, - or @ as a formula; this file gets emailed around. */
function csvCell(v: unknown): string {
  const t = v === null || v === undefined ? "" : String(v);
  const safe = /^[=+\-@]/.test(t) ? `'${t}` : t;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function buildRecordCsv(
  rows: AttorneyDirectoryRow[],
  uncovered: string[],
): string {
  const header = [
    "state_code",
    "area",
    "attorney_name",
    "email",
    "firm",
    "additional_emails",
    "needs_review",
    "review_reason",
    "attorney_report_name",
    "attorney_report_link",
    "link_source",
  ];
  const body = rows.map((r) =>
    [
      r.state_code,
      r.region ?? "(whole state)",
      r.full_name,
      r.contact_email ?? "",
      r.firm_label ?? "",
      (r.additional_emails ?? []).join("; "),
      r.attorney_needs_review || r.jurisdiction_needs_review ? "YES" : "",
      [r.attorney_review_reason, r.jurisdiction_review_reason]
        .filter(Boolean)
        .join(" | "),
      r.attorney_report_name ?? "",
      r.attorney_report_link ?? "",
      r.link_provenance ?? "",
    ]
      .map(csvCell)
      .join(","),
  );
  // The gap is part of the record. Naming it in the export stops it being lost when the file
  // is the thing someone circulates.
  const gap = uncovered.length
    ? ["", csvCell(`NO ATTORNEY ON FILE: ${uncovered.join(", ")}`)]
    : [];
  return [header.map(csvCell).join(","), ...body, ...gap].join("\n");
}

const AttorneyRecord: React.FC<Props> = ({ canWrite }) => {
  const queryClient = useQueryClient();
  const [testState, setTestState] = useState("TX");
  const [testZip, setTestZip] = useState("");
  const [tested, setTested] = useState<{
    rows: ResolvedAttorney[];
    state: string;
    zip: string;
  } | null>(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [edit, setEdit] = useState<AttorneyDirectoryRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const recordQ = useQuery({
    queryKey: ["attorney-record"],
    queryFn: () => listAttorneyDirectory(false),
  });

  const rows = useMemo(() => recordQ.data ?? [], [recordQ.data]);
  const uncovered = useMemo(() => uncoveredJurisdictions(rows), [rows]);
  const byState = useMemo(() => summariseByState(rows), [rows]);
  const flagged = useMemo(
    () =>
      rows.filter(
        (r) => r.attorney_needs_review || r.jurisdiction_needs_review,
      ),
    [rows],
  );
  const attorneyCount = useMemo(
    () => new Set(rows.map((r) => r.attorney_id)).size,
    [rows],
  );

  async function runTest() {
    setTesting(true);
    setTestError(null);
    try {
      const res = await resolveAttorneyJurisdiction(testState, testZip || null);
      setTested({ rows: res, state: testState, zip: testZip });
    } catch (e) {
      setTestError(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  }

  function exportCsv() {
    const blob = new Blob([buildRecordCsv(rows, uncovered)], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attorney-coverage-record-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function saveEdit() {
    if (!edit) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsertAttorneyDirectoryEntry({
        id: edit.attorney_id,
        fullName: edit.full_name,
        contactEmail: edit.contact_email,
        firmLabel: edit.firm_label,
        additionalEmails: edit.additional_emails,
        active: edit.attorney_active,
        needsReview: edit.attorney_needs_review,
        reviewReason: edit.attorney_review_reason,
      });
      await queryClient.invalidateQueries({ queryKey: ["attorney-record"] });
      setEdit(null);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (recordQ.isLoading) {
    return (
      <div className="text-sm text-gray-500">Loading the coverage record…</div>
    );
  }
  if (recordQ.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {(recordQ.error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ---- what is on file ------------------------------------------------ */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Attorneys" value={attorneyCount} />
        <Stat label="Coverage rows" value={rows.length} />
        <Stat
          label="Jurisdictions covered"
          value={`${byState.length} of ${ALL_JURISDICTIONS.length}`}
        />
        <Stat
          label="Need review"
          value={flagged.length}
          tone={flagged.length ? "amber" : "plain"}
        />
      </div>

      {/* ---- the gap, named ------------------------------------------------- */}
      {uncovered.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            No attorney on file for {uncovered.length}{" "}
            {uncovered.length === 1 ? "jurisdiction" : "jurisdictions"}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {uncovered.map((s) => (
              <span
                key={s}
                className="rounded border border-amber-300 bg-white px-2 py-0.5 text-xs font-semibold text-amber-900"
              >
                {s}
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs text-amber-800">
            A question from one of these states resolves to nobody and has to be
            assigned by hand.
          </p>
        </div>
      )}

      {/* ---- who would actually get a question ------------------------------ */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">
          Check who would get a question
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          Runs the real server resolver. Texas is split north/south, so the ZIP
          decides — the same county map that puts a member in a support region.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-600">
            State
            <select
              aria-label="State"
              className="mt-1 block rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={testState}
              onChange={(e) => setTestState(e.target.value)}
            >
              {ALL_JURISDICTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-600">
            ZIP (needed for split states)
            <input
              aria-label="ZIP code"
              className="mt-1 block w-32 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={testZip}
              placeholder="75001"
              onChange={(e) => setTestZip(e.target.value)}
            />
          </label>
          <Button onClick={runTest} variant="secondary" disabled={testing}>
            {testing ? "Checking…" : "Check"}
          </Button>
        </div>

        {testError && <p className="mt-3 text-sm text-red-700">{testError}</p>}

        {tested && !testError && (
          <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
            {tested.rows.length === 0 ? (
              <>
                <p className="font-semibold text-amber-900">
                  Nobody — this must be assigned by hand.
                </p>
                <p className="mt-1 text-xs text-gray-600">
                  {uncovered.includes(tested.state)
                    ? `No attorney is on file for ${tested.state}.`
                    : byState.find((s) => s.state_code === tested.state)
                          ?.is_split
                      ? `${tested.state} is split into areas and this ZIP could not be placed in one. ` +
                        `It deliberately does not guess.`
                      : `No active coverage resolved for ${tested.state}.`}
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold text-gray-900">
                  {tested.rows.map((r) => r.full_name).join(" and ")}
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
                  {tested.rows.map((r) => (
                    <li key={r.attorney_id}>
                      {r.contact_email}
                      {r.region ? ` — ${r.region}` : ""}
                      {r.needs_review && (
                        <span className="ml-1 text-amber-700">
                          (needs review)
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-gray-500">
                  Matched on{" "}
                  {tested.rows[0].resolution === "state"
                    ? "the state alone"
                    : "the member's ZIP → county → territory"}
                  .
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* ---- the rows we are unsure about ----------------------------------- */}
      {flagged.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-white">
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-2">
            <h3 className="text-sm font-semibold text-amber-900">
              {flagged.length}{" "}
              {flagged.length === 1 ? "row needs" : "rows need"} confirming
            </h3>
          </div>
          <ul className="divide-y divide-gray-100">
            {flagged.map((r) => (
              <li
                key={`${r.attorney_id}-${r.state_code}-${r.region ?? ""}`}
                className="px-4 py-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {r.state_code}
                      {r.region ? ` · ${r.region}` : ""} — {r.full_name}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-600">
                      {r.attorney_review_reason ?? r.jurisdiction_review_reason}
                    </p>
                  </div>
                  {canWrite && (
                    <Button variant="secondary" onClick={() => setEdit(r)}>
                      Resolve
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---- the record ------------------------------------------------------ */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Coverage by state
          </h3>
          <Button
            onClick={exportCsv}
            variant="secondary"
            disabled={!rows.length}
          >
            Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2">State</th>
                <th className="px-4 py-2">Area</th>
                <th className="px-4 py-2">Attorney</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Firm</th>
                {canWrite && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {byState.map((st) =>
                st.areas.map((area, ai) =>
                  area.attorneys.map((a, i) => (
                    <tr
                      key={`${st.state_code}-${area.region ?? ""}-${a.attorney_id}`}
                    >
                      {ai === 0 && i === 0 && (
                        <td
                          className="px-4 py-2 align-top font-semibold text-gray-900"
                          rowSpan={st.areas.reduce(
                            (n, x) => n + x.attorneys.length,
                            0,
                          )}
                        >
                          {st.state_code}
                          {st.is_split && (
                            <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-800">
                              split
                            </span>
                          )}
                        </td>
                      )}
                      {i === 0 && (
                        <td
                          className="px-4 py-2 align-top text-gray-700"
                          rowSpan={area.attorneys.length}
                        >
                          {area.region ?? (
                            <span className="text-gray-400">whole state</span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-2 text-gray-900">
                        {a.full_name}
                        {(a.attorney_needs_review ||
                          a.jurisdiction_needs_review) && (
                          <span
                            title={
                              a.attorney_review_reason ??
                              a.jurisdiction_review_reason ??
                              ""
                            }
                            className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
                          >
                            review
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {a.contact_email}
                        {a.additional_emails?.length > 0 && (
                          <div className="text-xs text-gray-400">
                            also {a.additional_emails.join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-500">
                        {a.firm_label}
                      </td>
                      {canWrite && (
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            className="text-xs font-medium text-blue-700 hover:underline"
                            onClick={() => setEdit(a)}
                          >
                            Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  )),
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- edit ------------------------------------------------------------ */}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              {edit.full_name}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {edit.state_code}
              {edit.region ? ` · ${edit.region}` : ""}
            </p>

            <label className="mt-4 block text-xs font-medium text-gray-700">
              Email
              <input
                aria-label="Email"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={edit.contact_email ?? ""}
                onChange={(e) =>
                  setEdit({ ...edit, contact_email: e.target.value })
                }
              />
            </label>
            <label className="mt-3 block text-xs font-medium text-gray-700">
              Firm
              <input
                aria-label="Firm"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={edit.firm_label ?? ""}
                onChange={(e) =>
                  setEdit({ ...edit, firm_label: e.target.value })
                }
              />
            </label>

            {edit.attorney_needs_review && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs text-amber-900">
                  {edit.attorney_review_reason}
                </p>
                <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-900">
                  <input
                    type="checkbox"
                    checked={!edit.attorney_needs_review}
                    onChange={(e) =>
                      setEdit({
                        ...edit,
                        attorney_needs_review: !e.target.checked,
                        // Clearing the flag clears the reason with it, so the row never keeps
                        // a stale explanation for a question that has been answered.
                        attorney_review_reason: e.target.checked
                          ? null
                          : edit.attorney_review_reason,
                      })
                    }
                  />
                  This is confirmed correct — clear the flag
                </label>
              </div>
            )}

            {saveError && (
              <p className="mt-3 text-sm text-red-700">{saveError}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setEdit(null)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={saveEdit} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: "amber" | "plain";
}> = ({ label, value, tone = "plain" }) => (
  <div
    className={`rounded-lg border p-3 ${
      tone === "amber"
        ? "border-amber-200 bg-amber-50"
        : "border-gray-200 bg-white"
    }`}
  >
    <p className="text-xs text-gray-500">{label}</p>
    <p
      className={`mt-0.5 text-xl font-bold ${
        tone === "amber" ? "text-amber-900" : "text-gray-900"
      }`}
    >
      {value}
    </p>
  </div>
);

export default AttorneyRecord;
